import React, { useState } from "react";
import styled from "styled-components";
import {
  LuHouse,
  LuCalendar,
  LuUsers,
  LuStore,
  LuCircleHelp,
  LuDollarSign,
  LuScissors,
  LuClock,
  LuMenu,
  LuListOrdered,
  LuSettings,
  LuLogOut,
  LuChevronRight,
  LuX,
  LuCoins
} from "react-icons/lu";
import { useNavigate, useLocation } from "react-router-dom";
import { useBarbeiro } from "../context/BarbeiroContext";
import { getAuth, signOut } from "firebase/auth";
import { app } from "../firebase";

// ====== Estilos da Barra Inferior ======
const BottomBar = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 72px; /* Altura fixa da barra */
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-around;
  z-index: 1000; /* Fica acima de tudo */
  padding-bottom: env(safe-area-inset-bottom);
  box-shadow: 0 -4px 20px rgba(0,0,0,0.03);
`;

const TabBtn = styled.button`
  flex: 1;
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  color: ${(p) => (p.$active ? "#3a79ed" : "#9ca3af")};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  
  svg {
    font-size: 24px;
    color: ${(p) => (p.$active ? "#3a79ed" : "#9ca3af")};
    transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform: ${(p) => (p.$active ? "translateY(-2px)" : "none")};
  }
`;

// ====== Estilos do Drawer (Menu Deslizante) ======

const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 998; /* Abaixo da BottomBar mas acima do conteúdo */
  opacity: ${(p) => (p.$isOpen ? 1 : 0)};
  visibility: ${(p) => (p.$isOpen ? "visible" : "hidden")};
  transition: opacity 0.3s ease;
  backdrop-filter: blur(2px);
`;

const Drawer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  /* O Drawer começa logo acima da BottomBar (72px + safe area) */
  bottom: calc(72px + env(safe-area-inset-bottom)); 
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 20px;
  z-index: 999;
  box-shadow: 0 -5px 25px rgba(0,0,0,0.1);
  
  /* Animação de deslize */
  transform: translateY(${(p) => (p.$isOpen ? "0" : "120%")});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 70vh;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  
  h3 { margin: 0; font-size: 1.1rem; color: #1f1a40; }
  button { background: none; border: none; color: #9ca3af; cursor: pointer; }
`;

const DrawerItem = styled.button`
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
  padding: 15px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 12px;
  font-size: 1rem;
  color: #4b5563;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:active {
    background: #e5e7eb;
  }

  svg { font-size: 20px; color: #3a79ed; }
  
  &.logout {
    background: #fef2f2;
    border-color: #fee2e2;
    color: #ef4444;
    svg { color: #ef4444; }
  }
`;

// Helper de rota ativa
const isPathActive = (pathname, base) =>
  pathname === base || pathname.startsWith(base + "/");

export default function TabsMobile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { barbeiro, loadingBarbeiro } = useBarbeiro();
  const auth = getAuth(app);

  const [menuOpen, setMenuOpen] = useState(false);

  if (loadingBarbeiro) return null;

  const isBarbeiro = barbeiro?.cargo === "barbeiro";

  // === CONFIGURAÇÃO DOS MENUS ===

  // 1. Abas Principais (Ficam na barra fixa)
  const TABS_MAIN = isBarbeiro
    ? [
        { key: "home", label: "Início", icon: LuHouse, path: "/" },
        { key: "agenda", label: "Agenda", icon: LuCalendar, path: "/agenda-barbeiro" },
        { key: "fila", label: "Fila", icon: LuClock, path: "/fila" },
        { key: "comissao", label: "Comissão", icon: LuDollarSign, path: "/comissao" },
        { key: "ajuda", label: "Ajuda", icon: LuCircleHelp, path: "/ajuda" },
      ]
    : [
        { key: "home", label: "Início", icon: LuHouse, path: "/" },
        { key: "agenda", label: "Agenda", icon: LuCalendar, path: "/agenda-horarios" },
        { key: "fila", label: "Fila", icon: LuClock, path: "/fila" },
        { key: "financeiro", label: "Financeiro", icon: LuCoins, path: "/financeiro" },
        { key: "menu", label: "Menu", icon: menuOpen ? LuX : LuMenu, action: "toggle" }, // Botão especial
      ];

  // 2. Itens do Menu Deslizante (Só para Proprietário)
  const DRAWER_ITEMS = [
    { label: "Equipe", icon: LuUsers, path: "/equipe" },
    { label: "Serviços", icon: LuScissors, path: "/servicos" },
    { label: "Minha Barbearia", icon: LuStore, path: "/minha-barbearia" },
    { label: "Detalhamento", icon: LuListOrdered, path: "/detalhamento" },
    { label: "Configurações", icon: LuSettings, path: "/configuracoes" },
    { label: "Ajuda", icon: LuCircleHelp, path: "/ajuda" },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setMenuOpen(false); // Fecha o menu ao navegar
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/login";
  };

  return (
    <>
      {/* Overlay Escuro (Clica fora para fechar) */}
      <Overlay $isOpen={menuOpen} onClick={() => setMenuOpen(false)} />

      {/* Menu Deslizante (Drawer) */}
      <Drawer $isOpen={menuOpen}>
        <DrawerHeader>
          <h3>Mais Opções</h3>
          <button onClick={() => setMenuOpen(false)}><LuX size={24}/></button>
        </DrawerHeader>
        
        {DRAWER_ITEMS.map((item, index) => (
          <DrawerItem key={index} onClick={() => handleNavigation(item.path)}>
            <item.icon />
            <span style={{flex: 1, textAlign: 'left'}}>{item.label}</span>
            <LuChevronRight size={16} color="#d1d5db"/>
          </DrawerItem>
        ))}

        <DrawerItem className="logout" onClick={handleLogout}>
          <LuLogOut />
          <span style={{flex: 1, textAlign: 'left'}}>Sair da Conta</span>
        </DrawerItem>
      </Drawer>

      {/* Barra Fixa Inferior */}
      <BottomBar>
        {TABS_MAIN.map((item) => {
          const active = item.path ? isPathActive(location.pathname, item.path) : menuOpen;
          
          return (
            <TabBtn
              key={item.key}
              type="button"
              onClick={() => {
                if (item.action === "toggle") {
                  setMenuOpen(!menuOpen);
                } else {
                  setMenuOpen(false); // Fecha menu se clicar em outra aba
                  navigate(item.path);
                }
              }}
              $active={active}
            >
              <item.icon />
              <span>{item.label}</span>
            </TabBtn>
          );
        })}
      </BottomBar>
    </>
  );
}